import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import {
  type ChatbotPosterReference,
  deleteChatbotPosterReference,
  getChatbotPosterReferences,
  updateChatbotPosterReference,
  uploadChatbotPosterReference,
} from "../../services/chatbot.service";
import {
  CHATBOT_REFERENCE_IMAGE_LIMIT,
  CHATBOT_REFERENCE_IMAGE_MAX_BYTES,
} from "./constants";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
// eslint-disable-next-line @typescript-eslint/no-require-imports
const { ReactSortable } = require("react-sortablejs");

const ACCEPTED_REFERENCE_TYPES = "image/jpeg,image/png,image/webp";

type SortablePosterReference = ChatbotPosterReference & { id: string };

export const ReferenceLibrary = ({ onBack }: { onBack: () => void }): React.ReactElement => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SortablePosterReference[]>([]);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const orderRef = useRef<SortablePosterReference[]>([]);
  const isAtReferenceLimit = items.length >= CHATBOT_REFERENCE_IMAGE_LIMIT;

  const load = () => getChatbotPosterReferences().then((nextItems) => {
    const normalizedItems = nextItems.map((item) => ({ ...item, id: item._id }));
    setItems(normalizedItems);
    orderRef.current = normalizedItems;
  });

  useEffect(() => {
    void load();
  }, []);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    const remainingSlots = Math.max(CHATBOT_REFERENCE_IMAGE_LIMIT - items.length, 0);
    if (!files.length) return;
    if (remainingSlots <= 0) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_full", { count: CHATBOT_REFERENCE_IMAGE_LIMIT }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }
    if (files.length > remainingSlots) {
      void Swal.fire({
        icon: "error",
        text: t("chatbot_reference_upload_limit_selection", {
          count: CHATBOT_REFERENCE_IMAGE_LIMIT,
          remaining: remainingSlots,
          selected: files.length,
        }),
        ...SWAL_SETTINGS,
      } as any);
      return;
    }

    const validFiles = files.filter((file) => file.size <= CHATBOT_REFERENCE_IMAGE_MAX_BYTES);
    if (!validFiles.length) return;

    for (const file of validFiles) {
      await uploadChatbotPosterReference(file);
    }
    await load();
  };

  const handleDelete = async (id: string): Promise<void> => {
    await deleteChatbotPosterReference(id);
    await load();
  };

  const persistOrder = async (nextItems: SortablePosterReference[]): Promise<void> => {
    setIsSavingOrder(true);
    try {
      await Promise.all(
        nextItems.map((item, index) => updateChatbotPosterReference(item._id, { display_order: index }))
      );
      await load();
    } finally {
      setIsSavingOrder(false);
    }
  };

  return (
    <div className="restaurant-chatbot-reference-view">
      <div className="restaurant-chatbot-session-view-header">
        <button
          className="btn btn-icon btn-outline-primary"
          type="button"
          onClick={onBack}
          aria-label={t("chatbot_back_to_chat")}
        >
          <i className="ti ti-arrow-left" />
        </button>
        <span>{t("chatbot_references")}</span>
      </div>

      <div className="restaurant-chatbot-reference-content">
        <div className="restaurant-chatbot-reference-grid">
          <ReactSortable
            list={items}
            setList={(nextItems: SortablePosterReference[]) => {
              setItems(nextItems);
              orderRef.current = nextItems;
            }}
            className="restaurant-chatbot-reference-sortable-list"
            handle=".restaurant-chatbot-reference-drag-handle"
            animation={180}
            disabled={isSavingOrder}
            chosenClass="sortable-chosen-placeholder"
            onEnd={() => {
              void persistOrder(orderRef.current);
            }}
          >
            {items.map((item, index) => (
              <div className="restaurant-chatbot-reference-card" key={item._id}>
                <div
                  className="restaurant-chatbot-reference-rank"
                  aria-label={index === 0 ? "Highest priority reference" : `Priority ${index + 1}`}
                >
                  {index + 1}
                </div>
                <img
                  src={item.image_url}
                  alt="Poster reference"
                  className="restaurant-chatbot-reference-card-image"
                />
                <button
                  className="restaurant-chatbot-reference-delete"
                  type="button"
                  onClick={() => {
                    void handleDelete(item._id);
                  }}
                  aria-label="Delete reference"
                >
                  <i className="ti ti-trash" />
                </button>
                <div className="restaurant-chatbot-reference-drag-handle" aria-hidden="true">
                  <i className="ti ti-grip-vertical" />
                </div>
              </div>
            ))}
          </ReactSortable>

          {!isAtReferenceLimit ? (
            <button
              className="restaurant-chatbot-reference-upload-tile"
              type="button"
              disabled={isSavingOrder}
              onClick={() => inputRef.current?.click()}
            >
              <i className="ti ti-upload" />
              <span>{t("chatbot_upload")}</span>
            </button>
          ) : null}
        </div>

        {!items.length && (
          <div className="restaurant-chatbot-reference-empty">
            {t("chatbot_upload_empty_state_text")}
          </div>
        )}

        <input
          ref={inputRef}
          hidden
          type="file"
          accept={ACCEPTED_REFERENCE_TYPES}
          multiple
          onChange={(event) => {
            void handleUpload(event);
          }}
        />
      </div>

      <div className="restaurant-chatbot-reference-footer">
        <i className="ti ti-info-circle" />
        <ul className="restaurant-chatbot-reference-footer-list">
          <li>{t("chatbot_reference_order_hint_priority")}</li>
          <li>{t("chatbot_reference_order_hint_precedence")}</li>
        </ul>
      </div>
    </div>
  );
};
